import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import React, { useMemo, useState } from 'react';
import { ROWS_PER_PAGE } from '../../utils/paginationUtils';
import { useDispatch, useSelector } from 'react-redux';
import { patchReservation } from '../../redux/slices/reservationsSlice';
import Paper from '@mui/material/Paper';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import NoData from '../NoData';
import CompleteDialog from './CompleteDialog';
import ReservationsDialog from './ReservationsDialog';
import WarningDialog from '../WarningDialog';
import { Link } from '@mui/material';

const Row = ({ isEventScope, reservation, setCompleteRes, setEditRes }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCancelOrderDialog, setOpenCancelWarningDialog] = useState(false);
  const isLoading = useSelector((state) => state.reservations.isLoading);
  const user = useSelector((state) => state.auth.user);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onComplete = () => {
    setCompleteRes(reservation);
    handleMenuClose();
  };

  const onEdit = () => {
    setEditRes(reservation);
    handleMenuClose();
  };

  const {
    id,
    orders_id,
    events_id,
    events_title,
    events_city,
    reservation_reference,
    created_at,
    updated_at,
    expiration,
    first_name,
    last_name,
    email,
    phone,
    status,
    notes,
  } = reservation;

  const statusChip = useMemo(() => {
    if (!status) return null;

    const chipLabel = status.replace('_', ' ');
    const chipColor =
      status === 'EXPIRED' || status === 'CANCELED' ? 'error' : status === 'COMPLETED' ? 'success' : 'warning';

    return (
      <Chip
        sx={{ mt: 1, borderRadius: 1 }}
        size="small"
        label={t(`${chipLabel}`)}
        variant="outlined"
        color={chipColor}
      />
    );
  }, [status, t]);

  const handleOpenCancelOrder = () => {
    setOpenCancelWarningDialog(true);
    handleMenuClose();
  };

  const handleCloseCancelOrder = () => {
    setOpenCancelWarningDialog(false);
  };

  const handleOnConfirmCancel = () => {
    dispatch(
      patchReservation({ ...(isEventScope ? { event_id: events_id } : {}), reservation_id: id, status: 'CANCELED' })
    )
      .unwrap()
      .then(() => {
        handleCloseCancelOrder();
        toast.success(t('Reservation cancelled successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
      })
      .catch((err) => {
        if (err.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
          handleCloseCancelOrder();
          toast.error(t('Reservation needs review. Please contact support before trying again.'), {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: false,
          });
        } else {
          toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
        }
      });
  };

  return (
    <>
      <TableRow
        sx={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
          '&:last-child td, &:last-child th': { borderBottom: 0 },
          '&:nth-of-type(odd)': (theme) => ({
            backgroundColor: theme.palette.action.hover,
          }),
        }}
      >
        <TableCell align="left">{id}</TableCell>
        <TableCell align="left">{reservation_reference}</TableCell>
        <TableCell align="left">{orders_id || '-'}</TableCell>

        <TableCell align="left">{dayjs(created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
        <TableCell align="left">{dayjs(updated_at).format('DD/MM/YYYY HH:mm')}</TableCell>
        {!isEventScope && (
          <TableCell>
            <Link underline="hover" href={`events/${events_id}`} sx={{ color: 'white', cursor: 'pointer' }}>
              {`${events_title} - ${events_city}`}
            </Link>
          </TableCell>
        )}
        <TableCell align="left">
          {first_name || last_name ? `${first_name || ''} ${last_name || ''}`.trim() : '-'}
        </TableCell>
        <TableCell>{email || '-'}</TableCell>
        <TableCell>{phone || '-'}</TableCell>
        <TableCell
          style={{
            maxWidth: 200,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {notes || '-'}
        </TableCell>
        <TableCell>{(expiration && dayjs(expiration).format('DD/MM/YYYY HH:mm')) || '-'}</TableCell>
        <TableCell align="center">{statusChip}</TableCell>
        <TableCell align="center">
          <IconButton size="small" aria-label="more options" onClick={handleMenu}>
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={!!anchorEl}
            onClose={handleMenuClose}
          >
            <MenuItem disabled={status !== 'PENDING'} onClick={onEdit}>
              {t('Edit')}
            </MenuItem>
            {user.role === 'cashier' && (
              <MenuItem disabled={status !== 'PENDING'} onClick={onComplete}>
                {t('Complete')}
              </MenuItem>
            )}
            <MenuItem disabled={status !== 'PENDING'} onClick={handleOpenCancelOrder}>
              {t('Cancel')}
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <WarningDialog
        title={t(`Cancel reservation`) + `#${id}`}
        content={t('By pressing confirm you will release the booked seats!.')}
        loading={isLoading}
        open={openCancelOrderDialog}
        onCancel={handleCloseCancelOrder}
        onConfirm={handleOnConfirmCancel}
      />
    </>
  );
};

export default function ReservationsTable({ eventId, reservations, pagination }) {
  const { t } = useTranslation();
  const { totalItems, page, limit, onPageChange, onRowsPerPageChange } = pagination;

  const [completeRes, setCompleteRes] = useState(null);
  const [editRes, setEditRes] = useState(null);

  const onCompleteClose = () => {
    setCompleteRes(null);
  };

  const onEditClose = () => {
    setEditRes(null);
  };

  const isEventScope = useMemo(() => !!eventId, [eventId]);

  return (
    <>
      {reservations.length ? (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer sx={{ mt: 2 }} component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow sx={{ whiteSpace: 'nowrap' }}>
                  <TableCell align="left">{t('ID')}</TableCell>
                  <TableCell align="left">{t('Reservation Ref')}</TableCell>
                  <TableCell align="left">{t('Order ID')}</TableCell>
                  <TableCell align="left">{t('Created')}</TableCell>
                  <TableCell align="left">{t('Updated')}</TableCell>
                  {!isEventScope && <TableCell align="left">{t('Event')}</TableCell>}
                  <TableCell align="left">{t('Name')}</TableCell>
                  <TableCell align="left">{t('Email')}</TableCell>
                  <TableCell align="left">{t('Phone')}</TableCell>
                  <TableCell align="left">{t('Notes')}</TableCell>
                  <TableCell align="left">{t('Expiration')}</TableCell>
                  <TableCell align="center">{t('Status')}</TableCell>
                  <TableCell align="center">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((r) => (
                  <Row
                    key={r.id}
                    isEventScope={isEventScope}
                    setCompleteRes={setCompleteRes}
                    setEditRes={setEditRes}
                    reservation={r}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page - 1}
            onPageChange={onPageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE}
            labelRowsPerPage={t('rowsPaginator')}
          />
        </Paper>
      ) : (
        <NoData />
      )}
      <CompleteDialog
        isEventScope={isEventScope}
        reservation={completeRes}
        open={!!completeRes}
        onClose={onCompleteClose}
      />
      <ReservationsDialog eventId={eventId} reservation={editRes} open={!!editRes} onClose={onEditClose} />
    </>
  );
}
